import React from 'react'
import {
  Section, Container, SectionLabel, SectionBody, SectionLabelTitle, PrinterIcon, CheckIcon, LinkButton,
} from './PrintInvoicesSection.style'
import { useForm } from 'react-hook-form'
import {
  ReceivementStatus, IntegrationFailEnum, EnumStatusReturnInvoice,
} from 'src/features/receivement/ReceivementTypes'
import { ConsultReceivementForm } from './ConsultReceivementForm'
import { StartActionBody } from './StartActionBody'
import { WarningBody } from './WarningBody'
import { useDispatch, useSelector } from 'react-redux'
import { actions } from 'src/features/receivement/ReceivementState'
import { State } from 'src/features/types'
import { PrintingInvoicesBody } from './PrintingInvoicesBody'
import {
  PrintInvoicesStepsEnum, PrintInvoicesTypes, PrintInvoicesTypesObject, SectionLabelState,
} from './PrintInvoicesSection.types'
import theme from 'src/styles/theme'
import { ConfirmPrintsBody } from './ConfirmPrintsBody'
import { receivementStatus } from 'src/utils/mappers/mapReceivementStatus'
import { useHistory } from 'react-router-dom'

interface PrintInvoicesSectionProps {
  dataToPrint?: any
  onConfirmSuccess?: (data: any) => void
}

export const PrintInvoicesSection = ({ dataToPrint, onConfirmSuccess }: PrintInvoicesSectionProps) => {
  const { register, handleSubmit, reset } = useForm()
  const dispatch = useDispatch()
  const history = useHistory()
  const currentPrintStep = useSelector((state: State) => state.receivement.currentPrintInvoicesStep)
  const currentReceivement = useSelector((state: State) => state.receivement.receivementInvoicesHtml)
  const currentInvoiceReturn = useSelector((state: State) => state.receivement.receivementInvoices?.invoicesReturn)
  const failedInvoices = useSelector((state: State) => state.receivement.receivementInvoices?.failedInvoices)
  const isTruckDriverAvailableReturn = useSelector((state: State) => state.receivement.receivementInvoices?.isTruckDriverAvailableReturn)
  const alreadyReleased = useSelector((state: State) => state.receivement.receivementInvoices?.alreadyReleased)

  const [currentInvoicesType, setCurrentInvoicesType] = React.useState(PrintInvoicesTypesObject[PrintInvoicesTypes.ACCEPTED])
  const [verifyStatus, setVerifyStatus] = React.useState(false)
  const [badge, setBadge] = React.useState(0)

  React.useEffect(() => {
    const disable = currentInvoiceReturn?.some((item) => item.status !== 'approved')

    if (currentInvoiceReturn && disable) {
      setVerifyStatus(true)
    } else {
      setVerifyStatus(false)
    }
  }, [currentInvoiceReturn])

  const setCurrentPrintStep = (step: PrintInvoicesStepsEnum) => {
    dispatch(actions.setCurrentPrintInvoicesStep(step))
  }

  const getDataToPrint = () => {
    if (dataToPrint) {
      reset()
      setCurrentPrintStep(PrintInvoicesStepsEnum.START_PRINT_OR_RETURN)
      dispatch(actions.getReceivementInvoicesByReceivementOrRequest({
        idReceivement: dataToPrint.idReceivement,
        idRequest: dataToPrint.idRequest,
      }))
    }
  }

  React.useEffect(() => {
    getDataToPrint()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataToPrint])

  const goBack = () => {
    reset()
    setCurrentPrintStep(PrintInvoicesStepsEnum.FILL_BADGE_AND_PASSWORD)
  }

  const onSubmit = (data) => {
    setBadge(data.badge)
    dispatch(actions.getReceivementInvoices(data))
  }

  const getInvoiceReturn = () => {
    const statusMap = {
      approved: EnumStatusReturnInvoice.APPROVED,
      pending: EnumStatusReturnInvoice.PENDING,
      rejected: EnumStatusReturnInvoice.REJECTED,
      invalid: EnumStatusReturnInvoice.INVALID,
      canceled: EnumStatusReturnInvoice.CANCELED,
      processing: EnumStatusReturnInvoice.PROCESSING,
      sefaz_approved: EnumStatusReturnInvoice.SEFAZ_APPROVED,
      contingency_approved: EnumStatusReturnInvoice.CONTINGENCY_APPROVED,
      approved_by_finances: EnumStatusReturnInvoice.APPROVED_BY_FINANCES,
      reproved_by_finances: EnumStatusReturnInvoice.REPROVED_BY_FINANCES,
    }

    const invoices = currentInvoiceReturn?.map((invoice) => (invoice ? `Chave nota fiscal: ${invoice.invoiceKeyOrigin} - Status: ${statusMap[invoice.status]}` : 'Nenhuma nota de devolução retornada'))
    return invoices
  }

  const resolveStartOrWarningBody = () => {
    const shouldStartPrinting = (currentReceivement?.idReceivementStatus === ReceivementStatus.FINISHED
    && ((isTruckDriverAvailableReturn !== undefined && isTruckDriverAvailableReturn !== null) || alreadyReleased))
    || (currentReceivement?.idReceivementStatus === ReceivementStatus.FAILED_WITH_EXTERNAL_SOURCES
      && currentReceivement?.integrationFail === IntegrationFailEnum.PURCHASE)

    let warningDescription: string | JSX.Element = 'PARA IMPRIMIR ALGUMA NOTA FISCAL, AGUARDE A FINALIZAÇÃO DO RECEBIMENTO'

    let formattedInvoiceKeys: string[] | undefined = []

    if (currentReceivement?.integrationFail === IntegrationFailEnum.BOTH || currentReceivement?.integrationFail === IntegrationFailEnum.FINANCES) {
      warningDescription = 'FALHA AO ENVIAR NOTAS AO FINANCEIRO. NOTAS FISCAIS:'
    }

    if ((currentReceivement?.idReceivementStatus === ReceivementStatus.WAITING_TRUCK_DRIVER_CONFIRMATION
      || currentReceivement?.idReceivementStatus === ReceivementStatus.PROCESSING_TRUCK_DRIVER_CONFIRMATION)
      && !alreadyReleased) {
      warningDescription = (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>AGUARDANDO CONFIRMAÇÃO DE MOTORISTA</span>
          <LinkButton
            onClick={() => history.push(`/receivement/${currentReceivement?.idReceivement}/finish`)}
            data-testid='redirect-to-finish-receivement-page-link-test-id'
          >
            /receivement/{currentReceivement?.idReceivement}/finish
          </LinkButton>
        </div>
      )
    }

    if (currentInvoiceReturn) {
      formattedInvoiceKeys = currentReceivement?.invoices.map((invoice) => `Nr. ${invoice.invoiceNumber} - Chave: ${invoice.invoiceAccessKey}`)
    }

    const onClickResend = (invoice): string => {
      dispatch(actions.resendToFinances({ chaveNf: invoice, idReceivement: currentReceivement?.idReceivement }))
      return 'request sent'
    }

    const onClickCheckStatus = () => {
      dispatch(actions.getOpenedInvoicesReturn(currentReceivement?.idReceivement!))
      return 'request sent'
    }

    return (
      shouldStartPrinting
        ? (
          <StartActionBody
            startActionButtonLabel='Iniciar processo de liberação'
            startAction={() => setCurrentPrintStep(PrintInvoicesStepsEnum.PRINT_INVOICES)}
            cancelAction={goBack}
            invoiceList={formattedInvoiceKeys}
            invoiceReturnList={getInvoiceReturn()}
            onClickCheckStatus={onClickCheckStatus}
            verifyStatus={verifyStatus}
            actionTestId='start-liberation-button'
            cancelTestId='cancel-liberation-button'
            hiddenCancelActionButton={dataToPrint}
          />
        )
        : (
          <WarningBody
            warningTitle={receivementStatus[ReceivementStatus[currentReceivement?.idReceivementStatus!]] || 'AGUARDANDO APROVAÇÃO DE GESTÃO DE ESTOQUE'}
            invoiceList={IntegrationFailEnum.FINANCES ? failedInvoices : []}
            warningDescription={warningDescription}
            returnButtonAction={dataToPrint ? getDataToPrint : goBack}
            returnButtonLabel={dataToPrint ? 'Recarregar' : 'Voltar'}
            onClickResend={onClickResend}
          />
        ))
  }

  const printInvoicesSectionBodies = [
    <ConsultReceivementForm
      onSubmit={handleSubmit(onSubmit)}
      register={register}
      formTitle='Para imprimir a(s) nota(s) fiscal(is) do recebimento informe:'
    />,
    resolveStartOrWarningBody(),
    <PrintingInvoicesBody
      setCurrentInvoicesType={setCurrentInvoicesType}
      currentInvoicesType={currentInvoicesType}
      currentInvoiceReturn={currentInvoiceReturn}
      currentReceivement={currentReceivement}
      observations={currentInvoicesType.observations}
      invoicesToPrint={[
        { invoices: currentReceivement?.acceptedInvoicesHtml, isPrinted: false, type: PrintInvoicesTypes.ACCEPTED },
        { invoices: currentReceivement?.returnInvoicesHtml, isPrinted: false, type: PrintInvoicesTypes.RETURN },
        { invoices: currentReceivement?.rejectedInvoicesHtml, isPrinted: false, type: PrintInvoicesTypes.REJECTED },
      ]}
    />,

    <ConfirmPrintsBody
      idReceivement={currentReceivement?.idReceivement}
      badge={badge}
      dataToPrint={dataToPrint}
      onConfirmSuccess={onConfirmSuccess}
    />,
  ]

  const resolveLabel = (): SectionLabelState => {
    if (currentPrintStep === PrintInvoicesStepsEnum.PRINT_INVOICES) return currentInvoicesType.sectionLabel

    if (currentPrintStep === PrintInvoicesStepsEnum.CONFIRM_PRINTS) {
      return { title: 'CONFIRMAÇÃO DE IMPRESSÃO', color: theme.colors.blue, icon: <CheckIcon /> }
    }
    return { title: 'IMPRIMIR NOTA FISCAL', color: theme.colors.blue, icon: <PrinterIcon /> }
  }

  const labelCurrentState: SectionLabelState = resolveLabel()

  return (
    <Container>
      <Section>
        <SectionLabel data-testid='print-section-label' backgroundColor={labelCurrentState.color}>
          {labelCurrentState.icon}
          <SectionLabelTitle>{labelCurrentState.title}</SectionLabelTitle>
        </SectionLabel>
        <SectionBody>
          {printInvoicesSectionBodies[currentPrintStep]}
        </SectionBody>
      </Section>
    </Container>
  )
}
